import { useRef, useState } from 'react';
import { Button } from './Button';
import { apiUpload, ClientApiError } from '../api/client';
import { downscaleImage } from '../lib/image';
import { decidePhotoBatchOutcome } from '../lib/photo-batch';
import type { ReferralPhoto } from '../../shared/types';

export function FieldPhotoUploader({
  referralId,
  onUploaded,
  onReload,
}: {
  referralId: string;
  onUploaded: (photo: ReferralPhoto) => void;
  onReload: () => void | Promise<void>;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function handle(files: File[]) {
    setError('');
    setBusy(true);
    // Reload the parent's referral once at the end of the batch, but only if
    // something actually happened that could make it stale: a photo landed,
    // or an upload's error indicates the referral's state (or the caller's
    // access to it) changed underneath the batch. A batch that only hit
    // ordinary per-file errors (bad type, too large) leaves the referral
    // untouched, so no reload is needed there. See decidePhotoBatchOutcome
    // for why that split is status-code based (403/404/409), not the
    // `referral_changed` JSON code the notes endpoint uses: the photos
    // endpoint never emits that code, it just returns whichever of those
    // statuses fits.
    let shouldReload = false;
    let failed = 0;
    let stoppedEarly = false;
    for (let index = 0; index < files.length; index++) {
      setStatus(`Uploading ${index + 1} of ${files.length}`);
      try {
        const blob = await downscaleImage(files[index]!);
        const { photo } = await apiUpload<{ photo: ReferralPhoto }>(
          `/api/referrals/${referralId}/photos`,
          blob,
          `field-${index + 1}.jpg`,
        );
        onUploaded(photo);
        shouldReload = true;
      } catch (caught) {
        const decision = decidePhotoBatchOutcome(caught);
        if (decision.reload) shouldReload = true;
        if (decision.countFailed) failed += 1;
        if (decision.stop) {
          stoppedEarly = true;
          setError(caught instanceof ClientApiError ? caught.message : 'A photo did not upload.');
          break;
        }
      }
    }
    setStatus('');
    // A batch that ran to completion despite some file-specific failures
    // (413/415/etc.) never set an error above, so report the tally now, the
    // same way the plumber intake flow reports its post-submit photoWarning.
    if (!stoppedEarly && failed > 0) {
      setError(failed === 1 ? 'One photo did not upload.' : `${failed} photos did not upload.`);
    }
    if (shouldReload) {
      await onReload();
    }
    setBusy(false);
  }

  return (
    <div style={{ marginTop: '0.5rem' }}>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        capture="environment"
        style={{ display: 'none' }}
        onChange={(event) => {
          void handle(Array.from(event.target.files ?? []));
          event.target.value = '';
        }}
      />
      {error && <div className="error-banner">{error}</div>}
      <Button
        type="button"
        variant="secondary"
        full
        disabled={busy}
        onClick={() => inputRef.current?.click()}
      >
        {busy ? 'Uploading' : 'Add photos'}
      </Button>
      {status && (
        <p className="muted" role="status">
          {status}
        </p>
      )}
    </div>
  );
}
